import React from "react";
import CompanyLogoSvg, { roundJigsawPath } from "./CompanyLogoSvg";

/** Stripe product images: square, at least 128×128 — 512 keeps the grid crisp. */
export const STRIPE_ICON_SIZE = 512;

const CORNER_BADGE_OUTER_R = 150;
const CORNER_BADGE_INNER_R = 129;
const CORNER_BADGE_EDGE = 8;

function cornerBadgeCenter() {
    const c = STRIPE_ICON_SIZE - CORNER_BADGE_EDGE - CORNER_BADGE_OUTER_R;
    return { cx: c, cy: c };
}

export const STRIPE_PRODUCT_ICON_VARIANTS = {
    WEB_PREMIUM: "web_premium",
    ASSISTANT_COINS: "assistant_coins",
    AI_ASSISTANT_CREDIT: "ai_assistant_credit",
    TUTOR_STANDARD: "tutor_standard",
};

/**
 * Stripe dashboard products (see docs/stripe/product-policy.md).
 * `web_premium` is the main consumer product; tutor_standard is the tutor plan.
 */
export const STRIPE_PRODUCT_ICONS = [
    {
        variant: STRIPE_PRODUCT_ICON_VARIANTS.WEB_PREMIUM,
        name: "Web Premium",
        summary: "Main product — puzzle unlock bundles live here as prices.",
        detail:
            "Starter Fun Bundle, Classic Challenge, and the 300 Coins pack. Studio mark plus a gold crown so it reads as the flagship.",
        fileBase: "stripe-web-premium-icon-512x512",
    },
    {
        variant: STRIPE_PRODUCT_ICON_VARIANTS.ASSISTANT_COINS,
        name: "Assistant coins",
        summary: "2000 coins · CA$1.99 — spent on jigsaw color baskets.",
        detail:
            "Gold coin stack with a four-color jigsaw piece, matching the color-basket unlock the coins pay for.",
        fileBase: "stripe-assistant-coins-icon-512x512",
    },
    {
        variant: STRIPE_PRODUCT_ICON_VARIANTS.AI_ASSISTANT_CREDIT,
        name: "AI Assistant Credit",
        summary: "200 AI credits · $1.99 — assistant usage.",
        detail: "Violet sparkle for the AI assistant with a gold credit coin.",
        fileBase: "stripe-ai-assistant-credit-icon-512x512",
    },
    {
        variant: STRIPE_PRODUCT_ICON_VARIANTS.TUTOR_STANDARD,
        name: "Tutor Standard",
        summary: "$4.99 / month or $49 / year — lesson tools for tutors.",
        detail:
            "Studio mark with an open book and pencil — vocabulary, worksheets, and classroom lessons.",
        fileBase: "stripe-tutor-standard-icon-512x512",
    },
];

/** Four-point sparkle with concave sides. */
function sparklePath(cx, cy, radius) {
    const q = radius * 0.18;
    return [
        `M ${cx} ${cy - radius}`,
        `Q ${cx + q} ${cy - q}, ${cx + radius} ${cy}`,
        `Q ${cx + q} ${cy + q}, ${cx} ${cy + radius}`,
        `Q ${cx - q} ${cy + q}, ${cx - radius} ${cy}`,
        `Q ${cx - q} ${cy - q}, ${cx} ${cy - radius}`,
        "Z",
    ].join(" ");
}

function CrownGlyph({ cx, cy, size, fill }) {
    const w = size;
    const h = size * 0.74;
    const left = cx - w / 2;
    const top = cy - h / 2;
    const baseTop = top + h * 0.72;
    return (
        <g fill={fill}>
            <path
                d={[
                    `M ${left} ${baseTop}`,
                    `L ${left + w * 0.06} ${top + h * 0.06}`,
                    `L ${left + w * 0.3} ${top + h * 0.42}`,
                    `L ${cx} ${top}`,
                    `L ${left + w * 0.7} ${top + h * 0.42}`,
                    `L ${left + w * 0.94} ${top + h * 0.06}`,
                    `L ${left + w} ${baseTop}`,
                    "Z",
                ].join(" ")}
            />
            <rect
                x={left + w * 0.04}
                y={baseTop + h * 0.1}
                width={w * 0.92}
                height={h * 0.18}
                rx={h * 0.07}
            />
        </g>
    );
}

function WebPremiumIcon({ id }) {
    const { cx, cy } = cornerBadgeCenter();
    return (
        <>
            <defs>
                <linearGradient id={id("crownGold")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FDE68A" />
                    <stop offset="55%" stopColor="#F59E0B" />
                    <stop offset="100%" stopColor="#B45309" />
                </linearGradient>
            </defs>

            <svg x="0" y="0" width={STRIPE_ICON_SIZE} height={STRIPE_ICON_SIZE} viewBox="0 0 512 512">
                <CompanyLogoSvg idPrefix={id("logo")} />
            </svg>

            <circle cx={cx} cy={cy} r={CORNER_BADGE_OUTER_R} fill="#0F172A" fillOpacity="0.92" />
            <circle
                cx={cx}
                cy={cy}
                r={CORNER_BADGE_INNER_R}
                fill={`url(#${id("crownGold")})`}
                stroke="#FFFFFF"
                strokeOpacity="0.7"
                strokeWidth="7.5"
            />
            <CrownGlyph cx={cx} cy={cy + 3} size={142} fill="#0F172A" />
        </>
    );
}

const COIN_CX = 256;
const COIN_CY = 196;
const COIN_R = 152;

function AssistantCoinsIcon({ id }) {
    const pieceSize = 148;
    /** Right + bottom tabs stick out by size × 20/124, so the visual box is wider than pieceSize. */
    const tabDepth = pieceSize * (20 / 124);
    const pieceX = COIN_CX - (pieceSize + tabDepth) / 2;
    const pieceY = COIN_CY - (pieceSize + tabDepth) / 2;
    const piece = roundJigsawPath(pieceX, pieceY, pieceSize, pieceSize, {
        top: 0,
        right: 1,
        bottom: 1,
        left: 0,
    });
    const pieceRight = pieceX + pieceSize + tabDepth;
    const pieceBottom = pieceY + pieceSize + tabDepth;
    const midX = (pieceX + pieceRight) / 2;
    const midY = (pieceY + pieceBottom) / 2;

    const quadrants = [
        { key: "tl", x: pieceX, y: pieceY, fill: "#DC2626" },
        { key: "tr", x: midX, y: pieceY, fill: "#0284C7" },
        { key: "bl", x: pieceX, y: midY, fill: "#16A34A" },
        { key: "br", x: midX, y: midY, fill: "#7C3AED" },
    ];

    return (
        <>
            <defs>
                <radialGradient id={id("scene")} cx="50%" cy="38%" r="78%">
                    <stop offset="0%" stopColor="#D2EC80" />
                    <stop offset="42%" stopColor="#96CC4E" />
                    <stop offset="100%" stopColor="#6EB040" />
                </radialGradient>
                <linearGradient id={id("coinFace")} x1="0.1" y1="0" x2="0.9" y2="1">
                    <stop offset="0%" stopColor="#FEF3C7" />
                    <stop offset="45%" stopColor="#FBBF24" />
                    <stop offset="100%" stopColor="#D97706" />
                </linearGradient>
                <linearGradient id={id("coinEdge")} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D97706" />
                    <stop offset="100%" stopColor="#92400E" />
                </linearGradient>
                <clipPath id={id("pieceClip")}>
                    <path d={piece} />
                </clipPath>
            </defs>

            {/* Matches Stripe Checkout brand green (#6EB040) at the edges. */}
            <rect width={STRIPE_ICON_SIZE} height={STRIPE_ICON_SIZE} fill={`url(#${id("scene")})`} />

            {/* Same-radius coins offset down read as a stack; only the bottom crescents show */}
            <circle cx={COIN_CX} cy={COIN_CY + 96} r={COIN_R} fill="#7C2D12" />
            <circle cx={COIN_CX} cy={COIN_CY + 50} r={COIN_R} fill={`url(#${id("coinEdge")})`} />

            <circle cx={COIN_CX} cy={COIN_CY} r={COIN_R} fill={`url(#${id("coinFace")})`} />
            <circle
                cx={COIN_CX}
                cy={COIN_CY}
                r={COIN_R}
                fill="none"
                stroke="#92400E"
                strokeOpacity="0.5"
                strokeWidth="7"
            />
            <circle
                cx={COIN_CX}
                cy={COIN_CY}
                r={COIN_R - 26}
                fill="none"
                stroke="#FFFBEB"
                strokeOpacity="0.65"
                strokeWidth="5"
            />

            <g clipPath={`url(#${id("pieceClip")})`}>
                {quadrants.map(({ key, x, y, fill }) => (
                    <rect
                        key={key}
                        x={x}
                        y={y}
                        width={(pieceRight - pieceX) / 2}
                        height={(pieceBottom - pieceY) / 2}
                        fill={fill}
                    />
                ))}
            </g>
            <path
                d={piece}
                fill="none"
                stroke="#FFFBEB"
                strokeWidth="9"
                strokeLinejoin="round"
            />
        </>
    );
}

function AiAssistantCreditIcon({ id }) {
    return (
        <>
            <defs>
                <linearGradient id={id("bg")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#8B5CF6" />
                    <stop offset="55%" stopColor="#6D28D9" />
                    <stop offset="100%" stopColor="#3B0764" />
                </linearGradient>
                <radialGradient id={id("glow")} cx="42%" cy="38%" r="60%">
                    <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.32" />
                    <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                </radialGradient>
                <linearGradient id={id("coinFace")} x1="0.1" y1="0" x2="0.9" y2="1">
                    <stop offset="0%" stopColor="#FEF3C7" />
                    <stop offset="50%" stopColor="#FBBF24" />
                    <stop offset="100%" stopColor="#D97706" />
                </linearGradient>
            </defs>

            <rect width={STRIPE_ICON_SIZE} height={STRIPE_ICON_SIZE} fill={`url(#${id("bg")})`} />
            <rect width={STRIPE_ICON_SIZE} height={STRIPE_ICON_SIZE} fill={`url(#${id("glow")})`} />

            <path d={sparklePath(228, 218, 152)} fill="#FFFFFF" />
            <path d={sparklePath(398, 122, 54)} fill="#FFFFFF" fillOpacity="0.88" />
            <path d={sparklePath(126, 384, 42)} fill="#FFFFFF" fillOpacity="0.7" />

            <circle cx="378" cy="378" r="104" fill="#3B0764" fillOpacity="0.85" />
            <circle
                cx="378"
                cy="378"
                r="90"
                fill={`url(#${id("coinFace")})`}
                stroke="#FFFBEB"
                strokeOpacity="0.7"
                strokeWidth="5"
            />
            <path d={sparklePath(378, 378, 56)} fill="#3B0764" />
        </>
    );
}

function OpenBookGlyph() {
    return (
        <g>
            <path
                d="M -58 10 L -58 -16 Q -58 -36 -4 -44 L -4 30 Q -58 18 -58 10 Z"
                fill="#065F46"
            />
            <path
                d="M 58 10 L 58 -16 Q 58 -36 4 -44 L 4 30 Q 58 18 58 10 Z"
                fill="#047857"
            />
            <path
                d="M -50 8 L -50 -10 Q -50 -26 -4 -34 L -4 24 Q -50 14 -50 8 Z"
                fill="#FFFFFF"
            />
            <path
                d="M 50 8 L 50 -10 Q 50 -26 4 -34 L 4 24 Q 50 14 50 8 Z"
                fill="#F8FAFC"
            />
            <rect x="-5" y="-42" width="10" height="70" rx="3" fill="#064E3B" />
            <line x1="-40" y1="-8" x2="-14" y2="-4" stroke="#94A3B8" strokeWidth="4" strokeLinecap="round" />
            <line x1="-40" y1="4" x2="-14" y2="8" stroke="#94A3B8" strokeWidth="4" strokeLinecap="round" />
            <line x1="-40" y1="16" x2="-18" y2="19" stroke="#94A3B8" strokeWidth="4" strokeLinecap="round" />
            <line x1="14" y1="-4" x2="40" y2="-8" stroke="#94A3B8" strokeWidth="4" strokeLinecap="round" />
            <line x1="14" y1="8" x2="40" y2="4" stroke="#94A3B8" strokeWidth="4" strokeLinecap="round" />
            <line x1="18" y1="19" x2="40" y2="16" stroke="#94A3B8" strokeWidth="4" strokeLinecap="round" />
        </g>
    );
}

function PencilGlyph() {
    return (
        <g>
            <polygon points="-62,-8 -62,8 -82,0" fill="#FED7AA" />
            <polygon points="-72,-4 -72,4 -82,0" fill="#334155" />
            <rect x="-62" y="-8" width="92" height="16" rx="2" fill="#F59E0B" />
            <rect x="-62" y="-8" width="92" height="5" rx="1" fill="#FDE68A" fillOpacity="0.85" />
            <rect x="30" y="-8" width="12" height="16" fill="#CBD5E1" />
            <rect x="42" y="-8" width="16" height="16" rx="3" fill="#FB7185" />
        </g>
    );
}

function TutorStandardIcon({ id }) {
    const { cx, cy } = cornerBadgeCenter();

    return (
        <>
            <defs>
                <linearGradient id={id("badge")} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#6EE7B7" />
                    <stop offset="100%" stopColor="#047857" />
                </linearGradient>
            </defs>

            <svg x="0" y="0" width={STRIPE_ICON_SIZE} height={STRIPE_ICON_SIZE} viewBox="0 0 512 512">
                <CompanyLogoSvg idPrefix={id("logo")} />
            </svg>

            <circle cx={cx} cy={cy} r={CORNER_BADGE_OUTER_R} fill="#0F172A" fillOpacity="0.92" />
            <circle
                cx={cx}
                cy={cy}
                r={CORNER_BADGE_INNER_R}
                fill={`url(#${id("badge")})`}
                stroke="#FFFFFF"
                strokeOpacity="0.75"
                strokeWidth="7.5"
            />
            <g transform={`translate(${cx} ${cy + 21}) scale(1.38)`}>
                <OpenBookGlyph />
            </g>
            <g transform={`translate(${cx + 10.5} ${cy - 27}) rotate(-38) scale(1.2)`}>
                <PencilGlyph />
            </g>
        </>
    );
}

const VARIANT_RENDERERS = {
    [STRIPE_PRODUCT_ICON_VARIANTS.WEB_PREMIUM]: WebPremiumIcon,
    [STRIPE_PRODUCT_ICON_VARIANTS.ASSISTANT_COINS]: AssistantCoinsIcon,
    [STRIPE_PRODUCT_ICON_VARIANTS.AI_ASSISTANT_CREDIT]: AiAssistantCreditIcon,
    [STRIPE_PRODUCT_ICON_VARIANTS.TUTOR_STANDARD]: TutorStandardIcon,
};

/**
 * 512×512 Stripe product image for one catalog product.
 * `idPrefix` must be unique per rendered instance so gradient ids stay isolated.
 */
export default function StripeProductIconSvg({ variant, idPrefix = "stripeIcon" }) {
    const Renderer = VARIANT_RENDERERS[variant];
    if (!Renderer) {
        return null;
    }
    const id = (name) => `${idPrefix}-${variant}-${name}`;
    const label = STRIPE_PRODUCT_ICONS.find((item) => item.variant === variant)?.name || variant;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={STRIPE_ICON_SIZE}
            height={STRIPE_ICON_SIZE}
            viewBox={`0 0 ${STRIPE_ICON_SIZE} ${STRIPE_ICON_SIZE}`}
            role="img"
            aria-label={`${label} — Stripe product icon`}
        >
            <Renderer id={id} />
        </svg>
    );
}
